import React, { useCallback, useEffect, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import PropTypes from 'prop-types';
import { useField } from 'formik';

import { hooks } from '@febos-io/design';

import './FormikInputFileDropZone.scss';
import classNames from 'classnames';
import useTranslation from '../../hooks/useTranslation';
import FormikInputFileDropZoneThumb from './FormikInputFileDropZoneThumb';
import { useUploadFilesMultipartToS3Callback } from '../../hooks/useUploadFilesMultipartToS3Callback';

const thumbsContainer = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: 1
};
const thumbInnerPercent = {
  display: 'block',
  width: '100%'
};

export function FormikInputFileDropZonePreview({
  id: defaultId,
  label,
  name,
  placeholder,
  maxHeight = '250px',
  accept = 'image/*',
  fileFolder = 'exam',
  group = 'exam',
  description = 'Drop Files here or click to upload',
  preview,
  onlyKey = true
}) {
  const { t } = useTranslation();

  const [, { value }, helper] = useField(name);
  const [files, setFiles] = useState(value);

  const updateFile = useCallback(async (file) => {
    if (onlyKey) {
      helper.setValue(file.fileKey);
    } else {
      helper.setValue(file);
    }
  }, [helper, onlyKey]);

  const {
    callback: uploadFiles,
    loading: isLoadingFiles,
    formatFile
  } = useUploadFilesMultipartToS3Callback(files, updateFile, fileFolder);

  const { id, ariaDescribedby } = hooks.useInput(defaultId, label);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    accept,
    disabled: isLoadingFiles,
    onDrop: async (result) => {
      if (!result || result.length === 0) {
        return;
      }
      const pendingFiles = result.map((file) => formatFile({ file, group }));
      let newFile = pendingFiles[0];
      newFile.preview = URL.createObjectURL(newFile.file);
      setFiles(newFile);
      await uploadFiles({ files: [newFile] });
    }
  });
  useEffect(() => {
    return () => {
      if (!files) {
        return;
      }
      if (files.preview) {
        URL.revokeObjectURL(files.preview);
      }
    };
  }, [files, files?.preview]);

  return (
    <section className="container">
      <div {...getRootProps({ className: 'dropzone' })}>
        <input
          id={id}
          placeholder={placeholder}
          {...(ariaDescribedby && {
            'aria-describedby': ariaDescribedby
          })}
          {...getInputProps()}
        />
        {(files || preview) ? (
          <div className="d-flex align-items-center justify-content-center flex-column">
            <FormikInputFileDropZoneThumb
              preview={files?.preview || preview}
              maxHeight={maxHeight}
            />
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center flex-column">
            <h5>{t(description)}</h5>
            <p className="text-secondary">
              {t('Drop files here or click to')}
              {' '}
              <a href="/" onClick={(e) => e.preventDefault()}>
                {t('browse')}
              </a>
              {' '}
              {t('thorough your machine')}
            </p>
          </div>
        )}
      </div>
      {files && files.meta && files.meta.progress > 0 && (
      <aside style={thumbsContainer}>
        <div className="progress" style={thumbInnerPercent}>
          <div
            className={classNames('progress-bar progress-bar-striped progress-bar-animated', {
              'bg-secondary': !files.meta.progress || files.meta.progress === 100
            })}
            role="progressbar"
            aria-valuenow={files.meta.progress}
            aria-valuemin="0"
            aria-valuemax="100"
            style={{
              width: `${files.meta.progress || 100}%`
            }}
          >
            {files.meta.progress && `${files.meta.progress} %`}
          </div>
        </div>
      </aside>
      )}
    </section>
  );
}

FormikInputFileDropZonePreview.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  classInput: PropTypes.string,
  className: PropTypes.string,
  accept: PropTypes.string,
  fileFolder: PropTypes.string,
  group: PropTypes.string,
  maxHeight: PropTypes.string,
  description: PropTypes.string,
  preview: PropTypes.string,
  onlyKey: PropTypes.bool
};

export default FormikInputFileDropZonePreview;
